import NiceModal, { useModal } from '@ebay/nice-modal-react'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PersonIcon from '@mui/icons-material/Person'
import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography
} from '@mui/material'
import {
  CompanyUsersRelation,
  CompanyUserWithUserAndRole
} from '@shared/types/companies.types'
import { useTranslation } from 'react-i18next'
import { getPaletteColor } from '../../utils/theme.utils'
import { getAvatarName, isDefaultRole } from '../../utils/user.utils'
import ContainedAvatar from '../ContainedAvatar'
import { ChangeJobTitleModal } from './ChangeJobTitleModal'
import { ChangeRoleModal } from './ChangeRoleModal'
import { ChangeUserRelationModal } from './ChangeUserRelationModal'

export const MemberProfileModal = NiceModal.create(
  ({
    member,
    hasUpdatePermission
  }: {
    member: CompanyUserWithUserAndRole
    hasUpdatePermission: boolean
  }) => {
    const modal = useModal()
    const { t } = useTranslation()
    const color = getPaletteColor(member.role.color)

    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        sx={{
          '& .MuiDialog-paper': {
            width: '37.25rem'
          }
        }}
      >
        <DialogTitle>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <span>
              {t('member_profile_modal.profile_of', {
                name: member.user.name,
                lastName: member.user.lastName
              })}
            </span>
            <IconButton onClick={() => modal.remove()} size='medium' edge='end'>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction='column'>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='left'
              padding='1.25rem'
              spacing={'2.25rem'}
            >
              {member.user.avatarUri ? (
                <ContainedAvatar
                  alt={member.user.name}
                  src={member.user.avatarUri}
                  sx={{ width: '7rem', height: '7rem' }}
                />
              ) : (
                <Avatar sx={{ width: '7rem', height: '7rem' }}>
                  {getAvatarName(
                    member.user.name || '',
                    member.user.lastName || ''
                  )}
                </Avatar>
              )}
              <Stack>
                <Typography variant='h6'>
                  {member.user.name} {member.user.lastName}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {member.user.email}
                </Typography>
              </Stack>
            </Stack>

            <Grid container padding='1.25rem' spacing={2} marginTop='3rem'>
              <Grid size={{ xs: 6 }}>
                <Typography variant='body2' color='text.secondary'>
                  {t('member_profile_modal.relation')}
                </Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  width='100%'
                >
                  <Stack direction='row' alignItems='center'>
                    {member.relation === CompanyUsersRelation.CLIENT && (
                      <HandshakeOutlinedIcon
                        sx={{
                          width: '1.5rem',
                          height: '1.5rem',
                          marginRight: '0.5rem',
                          color: 'text.secondary'
                        }}
                      />
                    )}
                    {member.relation === CompanyUsersRelation.PROVIDER && (
                      <LocalShippingOutlinedIcon
                        sx={{
                          width: '1.5rem',
                          height: '1.5rem',
                          marginRight: '0.5rem',
                          color: 'text.secondary'
                        }}
                      />
                    )}
                    {member.relation === CompanyUsersRelation.EMPLOYEE && (
                      <PersonIcon
                        sx={{
                          width: '1.5rem',
                          height: '1.5rem',
                          marginRight: '0.5rem',
                          color: 'text.secondary'
                        }}
                      />
                    )}
                    <Typography variant='body2'>
                      {t(`relation.${member.relation}`)}
                    </Typography>
                  </Stack>
                  {hasUpdatePermission && (
                    <IconButton
                      onClick={() => {
                        modal.remove()
                        NiceModal.show(ChangeUserRelationModal, {
                          companyId: member.companyId,
                          userId: member.user.id,
                          currentRelation: member.relation,
                          workspaceModal: false
                        })
                      }}
                      size='medium'
                    >
                      <EditIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
                    </IconButton>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant='body2' color='text.secondary'>
                  {t('member_profile_modal.role')}
                </Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  width='100%'
                >
                  <Chip
                    size='medium'
                    label={
                      isDefaultRole(member.role.name)
                        ? t(`role.${member.role.name}`)
                        : member.role.name
                    }
                    sx={{
                      backgroundColor: color.hex,
                      color: color.contrast
                    }}
                  />
                  <Typography variant='body2'></Typography>
                  {hasUpdatePermission && (
                    <IconButton
                      onClick={() => {
                        modal.remove()
                        NiceModal.show(ChangeRoleModal, {
                          companyId: member.companyId,
                          userId: member.user.id,
                          currentRole: member.role.name,
                          workspaceModal: false
                        })
                      }}
                      size='medium'
                    >
                      <EditIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
                    </IconButton>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant='body2' color='text.secondary'>
                  {t('member_profile_modal.job_title')}
                </Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  width='100%'
                >
                  <Typography variant='body2'>{member.jobTitle}</Typography>
                  {hasUpdatePermission && (
                    <IconButton
                      onClick={() => {
                        modal.remove()
                        NiceModal.show(ChangeJobTitleModal, {
                          companyId: member.companyId,
                          userId: member.user.id,
                          workspaceModal: false
                        })
                      }}
                      size='medium'
                    >
                      <EditIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
                    </IconButton>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => modal.remove()}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              borderRadius: '0.5rem',
              border: '1px solid rgba(159, 168, 218, 0.70)',
              background: 'custom.semiTransparentIndigo'
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)
